import { clamp, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useFlag } from '@/package';
import {
  usePcdCacheStore,
  usePcdRenderingStore,
  useTaskFrameStore,
} from '@/store';
import type { FrameData } from '@/type';

import {
  TICKWIDTH_BY_ZOOM,
  TICK_AND_TICK_INTERVAL_BY_ZOOM,
  ZOOM,
} from './constant';
import ToolPlayControllerContext from './context';
import type { ToolPlayControllerContextValue } from './type';

const ToolPlayControllerContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const {
    value: visibleOnlyMyFrame,
    setFalse: showAllFrame,
    setTrue: showOnlyMyFrame,
  } = useFlag(false);
  const {
    value: isPlaybarOpen,
    setFalse: closePlaybar,
    setTrue: openPlaybar,
    toggle: togglePlaybar,
  } = useFlag(true);
  const {
    allFrameList,
    currentFrame,
    isFrameFileLoading,
    selectedTask,
    getAssignedMeFrameList,
  } = useTaskFrameStore();
  const assignedMeFrameSeqMap = useMemo(
    () =>
      getAssignedMeFrameList().reduce(
        (acc, v) => ({ ...acc, [v.frameSeq]: true }),
        {} as {
          [K in FrameData['frameSeq']]: boolean;
        },
      ),
    [JSON.stringify(allFrameList)],
  );
  const frameSeqInParam = useParams<{ frameSeq: string }>().frameSeq;
  const currentFrameSeq = useMemo(
    /**
     * NOTE: useParams 를 보는 이유
     * currentFrame.frameSeq 는 fethcing 이후 갱신 되기 때문에
     * 프레임 앞뒤로 움직일 때 sync 가 맞지 않는 증상이 있어서
     * useParams 에 있는 frameSeq 를 우선적으로 사용함
     */
    () => +(frameSeqInParam ?? currentFrame?.frameSeq ?? 1),
    [currentFrame, frameSeqInParam],
  );
  const [pcdFileList, setPcdFileList] = useState([] as string[]);
  const { cacheStatus, getPcdPathMap } = usePcdCacheStore();
  const { pcdIndex, isPlaying, pcdInterval, play, setPcdInterval, stop } =
    usePcdRenderingStore();
  const navigate = useNavigate();
  const oldNewFrameSeqEntry = useRef([] as number[]);

  const moveFrameSeq = (frameSeq: number) => {
    frameSeq = clamp(frameSeq, 1, allFrameListLength);

    const frameSeqEntry = [currentFrameSeq, frameSeq];

    if (isEqual(oldNewFrameSeqEntry.current, frameSeqEntry)) return;

    oldNewFrameSeqEntry.current = frameSeqEntry;

    navigate(
      `/labeling/${selectedTask?.projectID}/${selectedTask?.id}/${frameSeq}`,
      {
        replace: true,
      },
    );

    return null;
  };
  const statusMap = useMemo(
    () =>
      selectedTask?.pcdPath ? { ...cacheStatus[selectedTask.pcdPath] } : {},
    [cacheStatus, selectedTask],
  );
  const next = () => {
    const nextFrameSeq = currentFrameSeq + pcdInterval;

    if (nextFrameSeq > allFrameListLength) return;

    moveFrameSeq(nextFrameSeq);
  };
  const nextMine = () =>
    currentFrame?.nextFrame && moveFrameSeq(currentFrame.nextFrame.frameSeq);
  const prev = () => {
    const prevFrameSeq = currentFrameSeq - pcdInterval;

    if (prevFrameSeq < 1) return;

    moveFrameSeq(prevFrameSeq);
  };
  const prevMine = () =>
    currentFrame?.prevFrame && moveFrameSeq(currentFrame.prevFrame.frameSeq);
  const goFirst = () => moveFrameSeq(1);
  const goLast = () => moveFrameSeq(allFrameListLength);
  const preloadedFrameSeqList = useMemo(() => {
    const preloadedFrameSeqList = [] as number[];

    for (let i = 0; i < pcdFileList.length; i++) {
      const fileName = pcdFileList[i];
      // const seq = +fileName.replace('.pcd', '');
      const seq = i + 1;

      if (!statusMap?.[fileName]?.loaded) continue;

      preloadedFrameSeqList[seq] = seq;
    }

    return preloadedFrameSeqList;
  }, [pcdFileList, statusMap]);
  const startFrameSeq = clamp(selectedTask?.startFrameSeq ?? 1, 1);
  const allFrameListLength = selectedTask?.fileFrameCount ?? 0;
  const [zoom, setZoom] = useState(ZOOM.DEFAULT);
  const [tick, tickInterval] = TICK_AND_TICK_INTERVAL_BY_ZOOM[zoom]; // [75, 1]
  const tickWidth = TICKWIDTH_BY_ZOOM[zoom];
  const tickerList = useMemo(() => {
    const tickerList = [] as number[];

    for (let i = 0; i < Math.ceil(allFrameListLength / tick); i++) {
      for (let j = 1; j <= tick / tickInterval; j++) {
        const v = j * tickInterval + i * tick;

        if (v + startFrameSeq > allFrameListLength) break;
        tickerList.push(v + startFrameSeq);
      }
    }

    return tickerList;
  }, [allFrameListLength, tick, tickInterval]);

  const $toolPlayController = useRef<HTMLDivElement>(null);

  const isAssignedMeFrameSeq = (frameSeq: FrameData['frameSeq']) =>
    !!assignedMeFrameSeqMap[frameSeq];

  useEffect(() => {
    if (!selectedTask) return;

    getPcdPathMap(selectedTask.pcdPath).then(pcdPathMap => {
      const pcdFileList = Object.entries(pcdPathMap).reduce((acc, cur) => {
        acc[+cur[0]] = cur[1];

        return acc;
      }, [] as string[]);

      setPcdFileList(pcdFileList);
    });
  }, [statusMap]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  return (
    <ToolPlayControllerContext.Provider<ToolPlayControllerContextValue>
      value={{
        allFrameListLength,
        index: pcdIndex,
        isFrameFileLoading,
        isPlaybarOpen,
        isPlaying,
        pcdInterval,
        preloadedFrameSeqList,
        tick,
        tickInterval,
        tickWidth,
        tickerList,
        visibleOnlyMyFrame,
        zoom,

        $toolPlayController,

        isAssignedMeFrameSeq,
        closePlaybar,
        goFirst,
        goLast,
        moveFrameSeq,
        next,
        nextMine,
        openPlaybar,
        play,
        prev,
        prevMine,
        setPcdInterval,
        setZoom,
        showAllFrame,
        showOnlyMyFrame,
        stop,
        togglePlaybar,
      }}
    >
      {children}
    </ToolPlayControllerContext.Provider>
  );
};

export default ToolPlayControllerContextProvider;
